import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ReactSVG } from 'react-svg';
import { useTrackVisibility } from 'react-intersection-observer-hook';

import { InnerContent } from '../../../modules/editor';
import { RootState } from '../../../modules';

import ButtonGroup from '../../../components/Button/ButtonGroup';
import ButtonToggleValue from '../../../components/Button/ButtonToggleValue';
import ButtonModal from '../../../components/Button/ButtonModal';
import InputSlider from '../../../components/Input/InputSlider';

import Modal from '../../../components/Modal/Modal';
import ImageCropper from '../../../components/ImageCropper/ImageCropper'
import useAlert from '../../../hooks/useAlert';
import { getPathAfterBuild } from '../../../util/file';

type Props = {
	innerContent: InnerContent;
	style: any;
	contentId: number;
	contentIndex: number;
	imageInfo: any;
	subIndex?: number;
	maxWidth: number;
};

function ImageToolbar({ innerContent, style, contentId, contentIndex, imageInfo, subIndex, maxWidth }: Props) {
	const dispatch = useDispatch();	
	const Alert = useAlert();

	const [ref, { isVisible }] = useTrackVisibility();

	const [showCropModal, setShowCropModal] = useState(false);
	const [fixedLeft, setFixedLeft] = useState('-1px');
	const [fixed, setFixed] = useState(false);
	const [loaded, setLoaded] = useState(false);
	
	const editor = useSelector((state: RootState) => state.editor);
	
	const iconImage = (path) => {
		const context = require.context('../../../assets/images/', true);
		return getPathAfterBuild(context, path);
	};

	const contentTarget = subIndex === undefined ? `${contentId}/content/${contentIndex}` : `${contentId}/content/${contentIndex}/${subIndex}`;
	const styleTarget = subIndex === undefined ? `${contentId}/style/${contentIndex}` : `${contentId}/style/${contentIndex}/${subIndex}`;

	const toolbarRef = useRef(null);

	useEffect(() => {	
		if (!loaded) {
			setLoaded(true);
			return;
		}
		if (isVisible) {
			setFixedLeft('-1px');
		} else {
			setFixedLeft(toolbarRef?.current?.getBoundingClientRect()?.x)
		}
		setFixed(!isVisible);
		setLoaded(true);
	}, [isVisible])

	const openCropModal = () => {		
		setShowCropModal(true);
	}

	const closeCropModal = () => {
		setShowCropModal(false);
	}
	
	return (
		<>
			<div className="toolbar-io" ref={ref}>io</div>
			<div className="toolbar-io" ref={toolbarRef}>io</div>
			<div className={`toolbar image ${fixed ? 'fixed' : ''}`} style={{left:fixedLeft}} >
				<div className="group">
					<ButtonGroup
						target={`${styleTarget}/image/textAlign`}
						value={style.textAlign}
						options={editor.option.ctaAlign}
						imagePath="icon/toolbar/button_align_"
						imageType="svg"
						// disabled={style.width === '100%'}
					/>
					<div className="btn-group">
						<ButtonToggleValue
							target={`${styleTarget}/image/fullWidth`}
							value={style.fullWidth}
							name="justify"
							imagePath="icon/toolbar/button_align_"
							imageType="svg"
						/>
					</div>
					<span className="divide" />
					<div className="btn-group">
						<ButtonModal
							target={`${contentTarget}`}
							value={innerContent}
							name="imageLink"
							imagePath="icon/toolbar/link"
							imageType="svg"
							title="URL을 입력하세요"
						/>
					</div>
					<div className="btn-group">
						<ButtonToggleValue
							target={`${contentTarget}/imageLink`}
							value=""
							name=""
							imagePath="icon/toolbar/unlink"
							imageType="svg"
							confirmText={Alert?.msg?.UNLINK_IMAGE}
						/>
					</div>
					<span className="divide" />
					<div className="btn-group">
						<button 
							className="btn"
							onClick={openCropModal}
						>
							<ReactSVG src={iconImage(`./icon/toolbar/imageEditor.svg`)} height="16" />
						</button>
					</div>
					<InputSlider 
						target={`${styleTarget}/image/width`}
						currentValue={style.width}
						name="imageSize"
						imagePath="icon/toolbar/"
						imageType="svg"
						imageInfo={imageInfo}
						maxWidth={maxWidth}
					/>
					<div className="btn-group">
						<ButtonToggleValue
							target={`${styleTarget}/image/borderRadius`}
							value={style.borderRadius}
							name="16px"
							imagePath="icon/toolbar/border_radius_"
							imageType="svg"
						/>
					</div>
					<div className="btn-group">
						<ButtonModal
							target={`${contentTarget}`}
							value={innerContent}
							name="imageAlt"
							imagePath="icon/toolbar/alt"
							imageType="svg"
							title="대체 텍스트를 입력하세요"
							valueType="alt"
						/>
					</div>
				</div>
			</div>
			<Modal
				show={showCropModal}
				close={closeCropModal}
			>
				<ImageCropper
					target={`${contentTarget}/imagePath`}
					src={innerContent?.imagePath}
					onClose={closeCropModal}
				/>
			</Modal>
		</>
	);
}

export default ImageToolbar;